import { useMemo } from 'react';
import { addDays } from 'date-fns';
import { useCircuitPrices } from './useCircuitPrices';
import { CircuitFormData } from '@/types/circuits/CircuitFormData';
import { EstimateFormData } from '@/types/estimates/EstimateFormData';

interface UseReservationSummaryProps {
  nbAdults: number;
  nbChildren: number;
  childrenAges: number[];
  parsedCircuit: CircuitFormData;
  selectedOptions: EstimateFormData['selectedEstimateOptions'];
  selectedArrivalDate?: Date | null;
}

export const useReservationSummary = ({
  nbAdults,
  nbChildren,
  childrenAges,
  parsedCircuit,
  selectedOptions,
  selectedArrivalDate,
}: UseReservationSummaryProps) => {
  const estimateCircuitDaysWithOnlySelectedOptions = useMemo(
    () =>
      parsedCircuit.circuitDays.map((day) => ({
        ...day,
        steps: day.steps.map((step) => {
          const getStandardBenefit = (benefitType: keyof typeof step) => {
            return selectedOptions[step[benefitType].id]
              ? selectedOptions[step[benefitType].id].benefitId ===
                step[benefitType].standardBenefit?.id
                ? step[benefitType].standardBenefit
                : step[benefitType].optionalBenefits.find(
                    ({ id }) =>
                      selectedOptions[step[benefitType].id].benefitId === id,
                  )
              : step[benefitType].standardBenefit;
          };

          const getHotelWithSelectedCategory = (
            hotelStandardBenefit: (typeof step)['accommodations']['standardBenefit'],
          ) => {
            if (!hotelStandardBenefit) return undefined;
            const category = hotelStandardBenefit.hotel?.HotelRoomCategory.find(
              ({ id }) =>
                selectedOptions[step.accommodations.id]?.hotelCategoryId === id,
            );
            if (!category) return hotelStandardBenefit;
            return {
              ...hotelStandardBenefit,
              hotel: {
                ...hotelStandardBenefit.hotel,
                commonPensionPricings:
                  hotelStandardBenefit.hotel?.commonPensionPricings ?? [],
                hotelSupplements:
                  hotelStandardBenefit.hotel?.hotelSupplements ?? [],
                associatedBenefits:
                  hotelStandardBenefit.hotel?.associatedBenefits ?? [],
                HotelRoomCategory: [category],
              },
            };
          };

          const getTransportWithSelectedCarCategoryIfAny = (
            transportStandardBenefit: (typeof step)['transport']['standardBenefit'],
          ) => {
            if (!transportStandardBenefit) return undefined;
            if (transportStandardBenefit.type !== 'VEHICLE')
              return transportStandardBenefit;
            const carCategory =
              transportStandardBenefit.carRental?.carCategories.find(
                ({ id }) =>
                  selectedOptions[step.transport.id]?.carCategoryId === id,
              );
            if (!carCategory) return transportStandardBenefit;
            return {
              ...transportStandardBenefit,
              carRental: {
                ...transportStandardBenefit.carRental,
                carCategories: [carCategory],
              },
            };
          };

          return {
            transport: {
              ...step.transport,
              standardBenefit: getTransportWithSelectedCarCategoryIfAny(
                getStandardBenefit('transport'),
              ),
              optionalBenefits: [],
            },
            accommodations: {
              ...step.accommodations,
              standardBenefit: getHotelWithSelectedCategory(
                getStandardBenefit('accommodations'),
              ),
              optionalBenefits: [],
            },
            activities: {
              ...step.activities,
              standardBenefit: getStandardBenefit('activities'),
              optionalBenefits: [],
            },
            meals: {
              ...step.meals,
              standardBenefit: getStandardBenefit('meals'),
              optionalBenefits: [],
            },
          };
        }),
      })) satisfies CircuitFormData['circuitDays'],
    [parsedCircuit.circuitDays, selectedOptions],
  );

  const targetDates = useMemo(() => {
    if (!selectedArrivalDate || !parsedCircuit.nbDays) return undefined;
    return Array.from({ length: parsedCircuit.nbDays }, (_, i) =>
      addDays(selectedArrivalDate, i),
    );
  }, [selectedArrivalDate, parsedCircuit.nbDays]);

  const {
    circuitDaysObject: reservationDaysObject,
    daysTrackingMultiDayBenefits: reservationDaysTrackingMultiDayBenefits,
  } = useCircuitPrices({
    days: estimateCircuitDaysWithOnlySelectedOptions,
    nbParticipants: nbAdults + nbChildren,
    circuitMarginRate: parsedCircuit.marginRate,
    nbChildren: nbChildren,
    childrenAges,
    targetDates,
  });

  return {
    estimateCircuitDaysWithOnlySelectedOptions,
    reservationDaysObject,
    reservationDaysTrackingMultiDayBenefits,
  };
};
